// Implements: F-GEN-022, F-UIX-019
/**
 * Mockup v3 P9 Modelos "Flujos" (v3 `:1866-1871`): the generation flows, read-only, each with
 * what it does (frontend dictionary, default P4-4), its current revision ("r3") and its steps in
 * the order they run, joined by arrows. The "Revisiones" history column is left out until an
 * endpoint serves it (default P4-4). A flow or a role the dictionary does not know reads as its
 * raw text (the flow id in mono). Pure presentation.
 */

import type { ReactElement } from "react";

import { useTranslation } from "../../../shared/i18n";
import type { FlowSummaryView } from "../../../types/reports";
import { flowWhatText, stepRoleText } from "./flowLabels";
import styles from "./Flows.module.css";

export interface FlowsTableProps {
  readonly flows: readonly FlowSummaryView[];
}

export function FlowsTable({ flows }: FlowsTableProps): ReactElement {
  const { t } = useTranslation();
  return (
    <div className={styles.page}>
      <p className={styles.notice}>{t("admin.flows.notice")}</p>
      <div className={styles.wrap}>
        <table className={styles.table} aria-label={t("admin.flows.table.label")}>
          <thead>
            <tr>
              <th scope="col">{t("admin.flows.column.flow")}</th>
              <th scope="col">{t("admin.flows.column.what")}</th>
              <th scope="col">{t("admin.flows.column.revision")}</th>
              <th scope="col">{t("admin.flows.column.steps")}</th>
            </tr>
          </thead>
          <tbody>
            {flows.map((flow) => (
              <tr key={flow.flow_id}>
                <th scope="row" className={styles.mono}>
                  {flow.flow_id}
                </th>
                <td>{flowWhatText(flow.flow_id)}</td>
                <td>
                  <span className={styles.revision}>{t("admin.flows.revision", { version: flow.version })}</span>
                </td>
                <td>
                  <ol className={styles.steps}>
                    {flow.step_roles.map((role, index) => (
                      // A flow may run the same role twice: the position keeps the keys apart.
                      <li key={`${String(index)}:${role}`} className={styles.step}>
                        <span className={styles.badge}>{stepRoleText(role)}</span>
                      </li>
                    ))}
                  </ol>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
